import type { TimelineItem } from '@/shared/types'

// Entries written before the app spoke Swedish, and the contact entries the API
// still writes, carry English bodies. They are translated here when read rather
// than rewritten in the database. Names inside a body stay as they are. A body
// that matches no known pattern is shown as stored, and every pattern is tied
// to its kind, so a note a seller happened to start with "Removed" is left alone.

const FIELD_LABEL: Record<string, string> = {
  name: 'namn',
  role: 'roll',
  email: 'e-post',
  phone: 'telefon',
  linkedin: 'LinkedIn',
}

const FIELDS = '(?:name|role|email|phone|linkedin)'
const EDITED = new RegExp(`^Edited (.+): (${FIELDS}(?:, ${FIELDS})*|no fields)$`, 's')

// The API's fallback when a contact has no name to log.
const name = (n: string) => (n === '(unnamed)' ? '(namnlös)' : n)

const fields = (list: string) =>
  list === 'no fields' ? 'inga fält' : list.split(', ').map(f => FIELD_LABEL[f]).join(', ')

// The outcome sheet folds the follow-up note into the body as its own line.
const nextLine = (line: string) =>
  line.replace(/^→ Next( \([^)]*\))?: /, (_, date: string | undefined) => `→ Nästa${date ?? ''}: `)

function translate(kind: TimelineItem['kind'], body: string): string {
  switch (kind) {
    case 'primary_changed': {
      if (body === 'Reverted to AI-picked primary') return 'Återställde till AI-vald kontakt'
      const m = /^Promoted (.+) as primary$/s.exec(body)
      return m ? `Gjorde ${m[1]} till primär kontakt` : body
    }
    case 'contact_edited': {
      const m = EDITED.exec(body)
      return m ? `Redigerade ${name(m[1])}: ${fields(m[2])}` : body
    }
    case 'contact_added': {
      const m = /^Added (.+)$/s.exec(body)
      return m ? `Lade till ${m[1]}` : body
    }
    case 'contact_removed': {
      const m = /^Removed (.+)$/s.exec(body)
      return m ? `Tog bort ${name(m[1])}` : body
    }
    case 'handover':
      return body === 'Reassigned in bulk' ? 'Massomfördelad' : body
    case 'created':
      return body === 'Added to CRM from admin Export tab' ? 'Tillagd i CRM från Export-fliken i admin' : body
    case 'meeting':
      if (body === 'Booked via stage change') return 'Bokad via stadieändring'
      if (body === 'Marked as booked') return 'Markerad som bokad'
      return body.split('\n').map(nextLine).join('\n')
    case 'call':
    case 'email':
      return body.split('\n').map(nextLine).join('\n')
    default:
      return body
  }
}

/** The entry's body as a Swedish reader should see it. */
export function historyBody(it: Pick<TimelineItem, 'kind' | 'body'>): string | undefined {
  return it.body ? translate(it.kind, it.body) : it.body
}
